/**
 * useOfflineMasterData Hook
 * Provides offline-aware master data fetching with 7-day TTL caching
 *
 * Usage:
 * const { breeds, species, genders, colors, loading } = useOfflineMasterData();
 */

import { useState, useEffect, useCallback } from 'react';
import { useOffline } from '../contexts/OfflineContext';
import {
  offlineMasterDataApi,
  type Breed,
  type Species,
  type Gender,
  type Color,
  type Location,
  type Source,
  type Ownership,
  type FeedType,
} from '../lib/offline';

interface MasterDataState {
  breeds: Breed[];
  species: Species[];
  genders: Gender[];
  colors: Color[];
  locations: Location[];
  sources: Source[];
  ownership: Ownership[];
  feedTypes: FeedType[];
}

interface UseOfflineMasterDataResult extends MasterDataState {
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  isCached: boolean;
  refresh: () => Promise<void>;
  prefetch: () => Promise<void>;
  getBreedName: (id: number) => string;
  getSpeciesName: (id: number) => string;
  getGenderName: (id: number) => string;
  getColorName: (id: number) => string;
  getLocationName: (id: number) => string;
  getSourceName: (id: number) => string;
  getOwnershipName: (id: number) => string;
  getFeedTypeName: (id: number) => string;
}

/**
 * Hook for offline-aware master data
 */
export function useOfflineMasterData(): UseOfflineMasterDataResult {
  const { isOnline } = useOffline();

  const [data, setData] = useState<MasterDataState>({
    breeds: [],
    species: [],
    genders: [],
    colors: [],
    locations: [],
    sources: [],
    ownership: [],
    feedTypes: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);

  // Fetch all master data
  const fetchMasterData = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true);
    setError(null);

    try {
      const [
        breeds,
        species,
        genders,
        colors,
        locations,
        sources,
        ownership,
        feedTypes,
      ] = await Promise.all([
        offlineMasterDataApi.getAllBreeds(forceRefresh),
        offlineMasterDataApi.getAllSpecies(forceRefresh),
        offlineMasterDataApi.getAllGenders(forceRefresh),
        offlineMasterDataApi.getAllColors(forceRefresh),
        offlineMasterDataApi.getAllLocations(forceRefresh),
        offlineMasterDataApi.getAllSources(forceRefresh),
        offlineMasterDataApi.getAllOwnership(forceRefresh),
        offlineMasterDataApi.getAllFeedTypes(forceRefresh),
      ]);

      setData({
        breeds,
        species,
        genders,
        colors,
        locations,
        sources,
        ownership,
        feedTypes,
      });

      // Check if data is cached
      const allCached = await offlineMasterDataApi.isAllCached();
      setIsCached(allCached);

      console.log('[useOfflineMasterData] Master data loaded:', {
        breeds: breeds.length,
        species: species.length,
        genders: genders.length,
        colors: colors.length,
        locations: locations.length,
        sources: sources.length,
        ownership: ownership.length,
        feedTypes: feedTypes.length,
      });
    } catch (err) {
      console.error('[useOfflineMasterData] Error fetching master data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch master data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchMasterData();
  }, [fetchMasterData]);

  // Prefetch when coming online
  useEffect(() => {
    if (isOnline && !loading) {
      // Background refresh
      offlineMasterDataApi.prefetchAll().catch(console.error);
    }
  }, [isOnline, loading]);

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchMasterData(true);
  }, [fetchMasterData]);

  // Prefetch function
  const prefetch = useCallback(async () => {
    await offlineMasterDataApi.prefetchAll();
    await fetchMasterData();
  }, [fetchMasterData]);

  // Helper functions to get names by ID
  const getBreedName = useCallback(
    (id: number): string => {
      const breed = data.breeds.find((b) => b.id === id);
      return breed?.name || `Breed #${id}`;
    },
    [data.breeds]
  );

  const getSpeciesName = useCallback(
    (id: number): string => {
      const sp = data.species.find((s) => s.id === id);
      return sp?.name || `Species #${id}`;
    },
    [data.species]
  );

  const getGenderName = useCallback(
    (id: number): string => {
      const gender = data.genders.find((g) => g.id === id);
      return gender?.name || `Gender #${id}`;
    },
    [data.genders]
  );

  const getColorName = useCallback(
    (id: number): string => {
      const color = data.colors.find((c) => c.id === id);
      return color?.name || `Color #${id}`;
    },
    [data.colors]
  );

  const getLocationName = useCallback(
    (id: number): string => {
      const location = data.locations.find((l) => l.id === id);
      return location?.name || `Location #${id}`;
    },
    [data.locations]
  );

  const getSourceName = useCallback(
    (id: number): string => {
      const source = data.sources.find((s) => s.id === id);
      return source?.name || `Source #${id}`;
    },
    [data.sources]
  );

  const getOwnershipName = useCallback(
    (id: number): string => {
      const own = data.ownership.find((o) => o.id === id);
      return own?.name || `Ownership #${id}`;
    },
    [data.ownership]
  );

  const getFeedTypeName = useCallback(
    (id: number): string => {
      const feedType = data.feedTypes.find((f) => f.id === id);
      return feedType?.name || `Feed Type #${id}`;
    },
    [data.feedTypes]
  );

  return {
    ...data,
    loading,
    error,
    isOffline: !isOnline,
    isCached,
    refresh,
    prefetch,
    getBreedName,
    getSpeciesName,
    getGenderName,
    getColorName,
    getLocationName,
    getSourceName,
    getOwnershipName,
    getFeedTypeName,
  };
}

export default useOfflineMasterData;
